/**
 * Sentry-Konfiguration für Frontend
 * 
 * @module client/src/lib/sentry
 */

import * as Sentry from "@sentry/react";

/**
 * Initialisiert Sentry für das Frontend
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || "production";
  const release = import.meta.env.VITE_SENTRY_RELEASE || "1.0.0";
  const isDev = import.meta.env.DEV;

  // Sentry nur initialisieren, wenn DSN gesetzt ist
  if (!dsn) {
    // In Production nicht in die Browser-Konsole spammen
    if (isDev) {
      console.warn("Sentry DSN nicht gesetzt - Error Tracking deaktiviert");
    }
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Performance Monitoring
    tracesSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in Production, 100% in Development
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session Replay für Fehler-Analyse
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Ignoriere bestimmte Fehler
    ignoreErrors: [
      // Browser-Erweiterungen
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "ChunkLoadError",
      // Netzwerk-Fehler
      "NetworkError",
      "Network request failed",
      "Failed to fetch",
    ],
    
    // Nur in Production senden
    enabled: environment === "production" || import.meta.env.VITE_SENTRY_ENABLED === "true",
    
    // Debug-Modus (nur in Development)
    debug: environment === "development",
    
    // Before-Send Hook für zusätzliche Filterung
    beforeSend(event, _hint) {
      // Ignoriere 404-Fehler
      if (event.exception?.values?.[0]?.value?.includes("404")) {
        return null;
      }
      
      // Ignoriere bestimmte Fehler-Codes
      if (event.tags?.statusCode === 404) {
        return null;
      }
      
      return event;
    },
    
    // Session Replay
    replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in Production
    replaysOnErrorSampleRate: 1.0, // 100% bei Fehlern
  });

  if (isDev) {
    console.log(`Sentry initialisiert für Environment: ${environment}`);
  }
}

/**
 * Setzt User-Kontext für Sentry
 */
export function setSentryUser(user: { id?: number | string; email?: string; username?: string }): void {
  Sentry.setUser({
    id: user.id?.toString(),
    email: user.email,
    username: user.username,
  });
}

/**
 * Entfernt User-Kontext (z.B. bei Logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

